import React, { useState, useEffect, useRef, useMemo, useContext } from "react";
import * as echarts from 'echarts';
import { Spinner, Link } from "@abb/abb-common-ux-react"; // Import Spinner and Link
import "./Bar-login-activity.css";
import { RootContext } from "../../../contexts/root-context";
import loginChartConfig from './Bar-login.json';
import { ERROR_LISTLogin } from "../../../strings"; // Import your error strings

const successfulLoginCodes = loginChartConfig.ualCodes.successful;
const unsuccessfulLoginCodes = loginChartConfig.ualCodes.unsuccessful;

function LoginActivityChart() {
  const chartRef = useRef(null);
  const [loginData, setLoginData] = useState([]);
  const [error, setError] = useState(false); // Changed to boolean for consistency
  const [isSpin, setisSpin] = useState(true); // State for spinner

  const {
    startTS,
    endTS,
    selectedSubstation,
    rimData,
    rimLaodingData,
    selectedDevice
  } = useContext(RootContext);

  // Helper function to process data
  const processData = (apiData) => {
    try {
      const loginCounts = {};

      const filteredData = apiData.filter(log => {
        const substationMatch = selectedSubstation ? log.substation === selectedSubstation : true;
        const deviceMatch = selectedDevice ? log.device_name === selectedDevice : true;
        return substationMatch && deviceMatch;
      });

      filteredData.forEach(log => {
        const time = new Date(log.Time);
        if (isNaN(time)) {
          console.warn("Invalid Time:", log.Time);
          return;
        }

        const hour = time.getHours();
        const paddedHour = hour.toString().padStart(2, '0');
        const timeKey = `${paddedHour}:00`;

        if (!loginCounts[timeKey]) {
          loginCounts[timeKey] = {
            time: timeKey,
            fullDateTime: time.toLocaleString(),
            Successful: 0,
            Unsuccessful: 0,
          };
        }

        const ualCode = parseInt(log.Ual);
        if (successfulLoginCodes.includes(ualCode)) {
          loginCounts[timeKey].Successful += 1;
        } else if (unsuccessfulLoginCodes.includes(ualCode)) {
          loginCounts[timeKey].Unsuccessful += 1;
        }
      });

      const chartData = Object.values(loginCounts).sort((a, b) =>
        parseInt(a.time) - parseInt(b.time)
      );

      setLoginData(chartData);
      setError(false); // Reset error on successful processing
    } catch (err) {
      console.error('Error processing login activity data:', err);
      setError(true); // Set error if processing fails
    } finally {
      setisSpin(false); // Always stop spinner after processing
    }
  };

  useEffect(() => {
    setisSpin(rimLaodingData); // Set spinner based on rimLaodingData
    if (!rimLaodingData && rimData) {
      processData(rimData);
    } else if (!rimLaodingData && !rimData) {
      // If loading is done but no data, likely an empty state
      setLoginData([]);
      setError(false); // No error, just empty data
      setisSpin(false);
    }
  }, [rimData, rimLaodingData, startTS, endTS, selectedSubstation, selectedDevice]);

  const chartOptions = useMemo(() => {
    if (loginData.length === 0) return {};

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: function (params) {
          const dataItem = loginData.find(item => item.time === params[0].name);
          if (!dataItem) return '';

          return `
            <strong>Login Activity</strong><br/>
            <span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:#00BFFF;"></span>
            Successful : ${dataItem.Successful}<br/>
            <span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:#FF4C4C;"></span>
            Unsuccessful : ${dataItem.Unsuccessful}<br/>
            <span style="margin-top:5px; color:#555; font-size:12px;">${dataItem.fullDateTime}</span>
          `;
        },
        backgroundColor: '#fff',
        borderColor: '#ccc',
        borderWidth: 1,
        padding: [10, 10, 10, 10],
        textStyle: { color: '#333' }
      },
      legend: {
        data: [
          { name: loginChartConfig.chartLabels.successfulSeriesName, icon: 'rect' },
          { name: loginChartConfig.chartLabels.unsuccessfulSeriesName, icon: 'rect' }
        ],
        top: loginChartConfig.chartLegend.top,
        right: loginChartConfig.chartLegend.right,
        itemGap: loginChartConfig.chartLegend.itemGap,
        textStyle: { color: '#333', fontSize: 12 }
      },
      grid: {
        left: loginChartConfig.chartGrid.left,
        right: loginChartConfig.chartGrid.right,
        bottom: loginChartConfig.chartGrid.bottom,
        top: loginChartConfig.chartGrid.top,
        containLabel: true,
        show: true,
        borderWidth: 0,
        splitLine: {
          show: true,
          lineStyle: {
            color: '#ddd',
            type: 'dashed',
            opacity: 0.7
          }
        }
      },
      xAxis: {
        type: loginChartConfig.chartLabels.xAxisType,
        data: loginData.map(item => item.time),
        axisLabel: { show: true, color: '#555', fontSize: 12 },
        axisTick: { show: false },
        axisLine: { show: true, lineStyle: { color: '#999' } },
        name: loginChartConfig.chartLabels.xAxisName,
        nameLocation: loginChartConfig.chartLabels.xAxisnameLocation,
        nameGap: 20,
        nameTextStyle: { color: '#333', fontSize: 14, fontWeight: 'bold' }
      },
      yAxis: {
        type: loginChartConfig.chartLabels.yAxisType,
        axisLabel: { show: true, color: '#555', fontSize: 12 },
        axisTick: { show: false },
        axisLine: { show: false },
        name: loginChartConfig.chartLabels.yAxisName,
        nameLocation: loginChartConfig.chartLabels.YAxisnameLocation,
        nameRotate: 90,
        nameGap: 35,
        nameTextStyle: { color: '#333', fontSize: 14, fontWeight: 'bold' },
        splitLine: {
          show: true,
          lineStyle: {
            color: '#ddd',
            type: 'dashed',
            opacity: 0.7
          }
        }
      },
      series: [
        {
          name: loginChartConfig.series.Sname,
          type: loginChartConfig.series.type,
          data: loginData.map(item => ({
            value: item.Successful,
            fullDateTime: item.fullDateTime
          })),
          itemStyle: { color: '#00BFFF' },
          barWidth: 'auto',
          barGap: '20%'
        },
        {
          name: loginChartConfig.series.Uname,
          type: loginChartConfig.series.type,
          data: loginData.map(item => ({
            value: item.Unsuccessful,
            fullDateTime: item.fullDateTime
          })),
          itemStyle: { color: '#FF4C4C' },
          barWidth: 'auto',
          barGap: '20%'
        }
      ]
    };
  }, [loginData]);

  useEffect(() => {
    // Only initialize chart if not loading, no error, and data is available
    if (!isSpin && !error && loginData.length > 0 && chartRef.current) {
      const chart = echarts.init(chartRef.current);
      chart.setOption(chartOptions);

      const handleResize = () => chart.resize();
      window.addEventListener('resize', handleResize);

      return () => {
        chart.dispose();
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [chartOptions, loginData, isSpin, error]);

  let content;
  if (isSpin) {
    content = <Spinner style={{ margin: "auto" }} color="dark-grey" size="small" />;
  } else if (error) {
    content = (
      <span className="error-text">
        {ERROR_LISTLogin.ERROR_FETCH_DATA}
        <br />
        <Link style={{ color: "#1f1f1f" }} onClick={() => processData(rimData)}>
          Retry
        </Link>
      </span>
    );
  } else if (loginData?.length > 0) {
    content = <div ref={chartRef} className="login-activity-chart" />;
  } else {
    content = <p className="text-error">{ERROR_LISTLogin.LOGIN_SUMMARY_EMPTY_DATA}</p>;
  }

  return (
    <div className="login-activity-chart-container">
      {content}
    </div>
  );
}

export default LoginActivityChart;